import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';

const RowOptions = ({
  title,
  options = [],
  selectedOption = [],
  border = false,
  onChange = () => {},
  availableOptions = [],
  visibleIdsAvailable = [],
  hasRVGarages = false,
  onRVSelected = () => {},
  isRVAvailable = false,
  isRVSelected = false,
}) => {
  const [alertMessage, setAlertMessage] = useState('');

  const handleChange = (value) => {
    if (selectedOption.includes(value)) {
      onChange(selectedOption.filter((c) => c !== value));
    } else {
      onChange([...selectedOption, value]);
    }
  };

  const handleOptionClick = (option) => {
    if (
      !(
        availableOptions.includes(option) &&
        visibleIdsAvailable.includes(option)
      )
    ) {
      setAlertMessage(`${option} is disabled`);
      return;
    }
    handleChange(option);
  };

  const handleOptionKeyDown = (e, option) => {
    if (
      !(
        availableOptions.includes(option) &&
        visibleIdsAvailable.includes(option)
      ) &&
      (e.key === 'Enter' || e.key === ' ')
    ) {
      e.preventDefault();
      setAlertMessage(`${option} is disabled`);
    }
  };

  const handleRVClick = () => {
    if (!isRVAvailable) {
      setAlertMessage('RV option is disabled');
      return;
    }
    onRVSelected();
  };

  const handleRVKeyDown = (e) => {
    if (!isRVAvailable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setAlertMessage('RV option is disabled');
    }
  };

  const [lastPressedButton, setLastPressedButton] = useState('');
  const announcementRef = useRef(null);

  useEffect(() => {
    if (lastPressedButton) {
      announcementRef.current.textContent = `${lastPressedButton}`;
    }
  }, [lastPressedButton]);

  return (
    <div
      className={cx('pt-8 pb-6 flex flex-col w-full', {
        'border-b border-action-blue': border,
      })}
    >
      <div className={cx('flex flex-col md:flex-row md:items-center w-full')}>
        <span className='w-full md:w-2/5 lg:w-1/2 mb-5 md:mb-0 text-action-blue text-2xl leading-none pt-1 font-trade-gothic-20'>
          {title}
        </span>
        <div className='w-full md:w-3/5 lg:w-1/2 flex'>
          {options.map((option, index) => (
            <button
              key={option.toString()}
              className={cx(
                'flex items-center justify-center border rounded-lg cursor-pointer w-10 h-10',
                {
                  'mr-5': true, // options.length - 1 !== index,
                  'pointer-events-none opacity-50': !(
                    availableOptions.includes(option) &&
                    visibleIdsAvailable.includes(option)
                  ),
                  'border-action-blue text-action-blue bg-white':
                    availableOptions.includes(option) &&
                    !selectedOption.includes(option),
                  'border-action-blue text-white bg-action-blue':
                    availableOptions.includes(option) &&
                    selectedOption.includes(option),
                  'border-mattamy-search-gray text-mattamy-search-gray bg-white':
                    !(
                      availableOptions.includes(option) &&
                      visibleIdsAvailable.includes(option)
                    ),
                },
              )}
              onClick={() => {
                handleOptionClick(option);
                setLastPressedButton(
                  `${option} ${title} ${
                    !selectedOption.includes(option) ? 'checked' : ''
                  }`,
                );
              }}
              onKeyDown={(e) => handleOptionKeyDown(e, option)}
              aria-label={
                option
                  ? option +
                    ' ' +
                    `${title}` +
                    (index === options.length - 1 ? '+' : '') +
                    (availableOptions.includes(option)
                      ? ''
                      : ' option disabled')
                  : ''
              }
              role='checkbox'
              aria-checked={selectedOption.includes(option)}
              aria-hidden={selectedOption.includes(option)}
              disabled={
                !availableOptions.includes(option) ||
                !visibleIdsAvailable.includes(option)
              }
            >
              {option}
              {index === options.length - 1 ? '+' : ''}
            </button>
          ))}
          {hasRVGarages && (
            <button
              key={'RV'}
              className={cx(
                'flex items-center justify-center rounded-lg cursor-pointer w-10 h-10',
                { 'pointer-events-none opacity-50': !isRVAvailable },
              )}
              onClick={() => {
                handleRVClick();
                setLastPressedButton(
                  `RV option ${!isRVSelected ? 'checked' : ''}`,
                );
              }}
              onKeyDown={handleRVKeyDown}
              aria-label={isRVAvailable ? 'RV option' : 'RV option disabled'}
              role='checkbox'
              aria-checked={isRVSelected}
              aria-hidden={isRVSelected}
              disabled={!isRVAvailable}
            >
              <div
                className={cx(
                  'flex items-center border justify-center rounded-lg w-10 h-10 ',
                  {
                    'border-action-blue md:hover:bg-action-blue md:hover:text-white':
                      isRVAvailable,
                    'bg-white text-action-blue': isRVAvailable && !isRVSelected,
                    'bg-action-blue text-white': isRVAvailable && isRVSelected,
                    'border-mattamy-search-gray text-mattamy-search-gray bg-white':
                      !isRVAvailable,
                  },
                )}
              >
                {'RV'}
              </div>
            </button>
          )}
        </div>
      </div>
      {alertMessage && <div className='text-red-600 mt-2'>{alertMessage}</div>}
      <div
        aria-live='polite'
        aria-atomic='true'
        ref={announcementRef}
        style={{ position: 'absolute', left: '-9999px' }}
      />
    </div>
  );
};

export default RowOptions;
